import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid } from '@material-ui/core';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import { Form } from 'antd';
import CB from 'antd/lib/checkbox/Checkbox';
import CircularProgress from '@material-ui/core/CircularProgress';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';

/**
 * StepPaymentApproval - Step 5: Terms, payment options, and submit
 */
const StepPaymentApproval = ({
  packageDetails,
  quoteDetails,
  basePrice,
  totalBaggagePrice = 0,
  paymentsCount = 1,
  onPaymentsChange,
  calculateTotalPrice,
  calculatePaymentPrice,
  onOpenLink,
  finishProgress = false,
  currency = 'USD',
  form,
  onSubmit,
  marketingConsentGiven = false
}) => {
  const { t } = useTranslation();
  const [errors, setErrors] = useState([]);
  const currencySymbol = currency === 'USD' ? '$' : currency;
  const totalPrice = calculateTotalPrice ? calculateTotalPrice(basePrice, totalBaggagePrice, paymentsCount) : basePrice;

  const handleSubmit = async () => {
    if (!form) {
      onSubmit && onSubmit();
      return;
    }

    try {
      await form.validateFields(['check1', 'check2']);
      setErrors([]);
      onSubmit && onSubmit();
    } catch (errorInfo) {
      const errorMessages = errorInfo.errorFields?.map(field => ({
        name: field.name[0],
        message: field.errors[0]
      })) || [];
      setErrors(errorMessages);
    }
  };

  const handleCheckboxChange = () => {
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  return (
    <div className="step-payment-approval" role="region" aria-label={t('reservation.steps.paymentApproval.ariaLabel')}>
      {/* Terms Checkboxes */}
      <div className="payment-checkbox-section" role="group" aria-label={t('reservation.steps.paymentApproval.checkboxAriaLabel')}>
        {/* Checkbox 1: Terms and Accommodation */}
        <div className="payment-checkbox-item required">
          <Form.Item
            name="check1"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value ? Promise.resolve() : Promise.reject(new Error(t('reservation.steps.paymentApproval.termsCheckbox.validation.bookingTerms'))),
              },
            ]}
          >
            <CB aria-label={`${t('reservation.steps.paymentApproval.termsCheckbox.approve')} ${t('reservation.steps.paymentApproval.termsCheckbox.bookingTerms')} ${t('reservation.steps.paymentApproval.termsCheckbox.and')} ${t('reservation.steps.paymentApproval.termsCheckbox.cancellationPolicy')}`} onChange={handleCheckboxChange}>
              {t('reservation.steps.paymentApproval.termsCheckbox.approve')} &nbsp;
              <a
                href={packageDetails?.data?.policies?.booking?.link}
                className="payment-link"
                onClick={(e) => onOpenLink && onOpenLink(e, packageDetails?.data?.policies?.booking?.link)}
              >
                {t('reservation.steps.paymentApproval.termsCheckbox.bookingTerms')}
              </a>
              {" "}{t('reservation.steps.paymentApproval.termsCheckbox.and')}{" "}
              <a
                href={packageDetails?.data?.policies?.cancellation?.link}
                className="payment-link"
                onClick={(e) => onOpenLink && onOpenLink(e, packageDetails?.data?.policies?.cancellation?.link)}
              >
                {t('reservation.steps.paymentApproval.termsCheckbox.cancellationPolicy')}
              </a> {t('reservation.steps.paymentApproval.termsCheckbox.cancellationPolicySuffix')}
            </CB>
          </Form.Item>
        </div>

        {/* Checkbox 2: Cancellation Policy */}
        <div className="payment-checkbox-item required">
          <Form.Item
            name="check2"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value ? Promise.resolve() : Promise.reject(new Error(t('reservation.steps.paymentApproval.termsCheckbox.validation.cancellationPolicy'))),
              },
            ]}
          >
            <CB aria-label={`${t('reservation.steps.paymentApproval.termsCheckbox.approve')} ${t('reservation.steps.paymentApproval.termsCheckbox.flightAndAccommodation')}`} onChange={handleCheckboxChange}>
             {t('reservation.steps.paymentApproval.termsCheckbox.approve')} &nbsp;

              <a
                href={packageDetails?.data?.policies?.property?.link}
                className="payment-link"
                onClick={(e) => onOpenLink && onOpenLink(
                  e,
                  packageDetails?.data?.policies?.property?.link,
                  quoteDetails?.properties &&
                  quoteDetails?.properties.length > 0 &&
                  quoteDetails?.properties[0]?.inventory &&
                  quoteDetails?.properties[0]?.inventory.length > 0 &&
                  quoteDetails?.properties[0]?.inventory[0]?.remarks &&
                  quoteDetails?.properties[0]?.inventory[0]?.remarks !== ''
                    ? (`<p>${t('reservation.steps.paymentApproval.termsCheckbox.hotelTermsPrefix')}</p><p style="direction:ltr;text-align:left">${quoteDetails?.properties[0]?.inventory[0]?.remarks}</p>`)
                    : ''
                )}
              >
                {t('reservation.steps.paymentApproval.termsCheckbox.flightAndAccommodation')}
              </a>
            </CB>
          </Form.Item>
        </div>

        {/* Checkbox 3: Marketing Consent - only show if not already checked in step 1 */}
        {!marketingConsentGiven && (
          <div className="payment-checkbox-item optional">
            <Form.Item
              name="marketing_consent"
              valuePropName="checked"
            >
              <CB
                aria-label={t('reservation.steps.paymentApproval.marketingConsent.ariaLabel')}
                onChange={(e) => {
                  if (e.target.checked) {
                    form.setFieldsValue({ consent_version: 'v2_short' });
                  }
                }}
              >
                {t('reservation.steps.paymentApproval.marketingConsent.text')} <br />{t('reservation.steps.paymentApproval.marketingConsent.secondLine')}
              </CB>
            </Form.Item>
            <Form.Item
              name="consent_version"
              className="wizard-hidden-field"
            >
              <input type="hidden" />
            </Form.Item>
          </div>
        )}

      </div>

      {/* Payment Options */}
      <div className="payment-installments-section" role="group" aria-labelledby="payments-label">
        <div className="payment-installments-label" id="payments-label">{t('reservation.steps.paymentApproval.payments.label')}</div>

        <Form.Item
          name="payments_count"
        >
          <Select
            variant="outlined"
            defaultValue={1}
            value={paymentsCount}
            aria-labelledby="payments-label"
            aria-describedby="payment-total"
            className="payment-select"
            onChange={(e) => onPaymentsChange && onPaymentsChange(e.target.value)}
          >
            <MenuItem value={1}>{t('reservation.steps.paymentApproval.payments.singlePayment')}</MenuItem>
            {[2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((payments) => (
              <MenuItem key={payments} value={payments}>
                {t('reservation.steps.paymentApproval.payments.multiplePayments', {
                  count: payments,
                  symbol: currencySymbol,
                  amount: calculatePaymentPrice ? calculatePaymentPrice(basePrice, totalBaggagePrice, payments) : (basePrice / payments).toFixed(2)
                })}
                {' '}{t('reservation.steps.paymentApproval.payments.equals')}{' '}
                {currencySymbol}
                {calculateTotalPrice ? calculateTotalPrice(basePrice, totalBaggagePrice, payments) : basePrice}
              </MenuItem>
            ))}
          </Select>
        </Form.Item>

        {/* Total Price Display */}
        <div className="payment-total" id="payment-total" aria-live="polite">
          {t('reservation.steps.paymentApproval.payments.total', { symbol: currencySymbol, total: totalPrice })}
        </div>
      </div>

      {/* Footnote */}
      <div className="payment-footnote" role="note">
        {t('reservation.steps.paymentApproval.footnote')}
      </div>

      {/* Error Display - Above Button */}
      {errors.length > 0 && (
        <div className="step-error-container" role="alert" aria-live="polite">
          <div className="step-error-title">
            <ErrorOutlineIcon className="step-error-icon" aria-hidden="true" />
            {t('reservation.steps.bookerDetails.errors.fixErrors')}
          </div>
          <ul className="step-error-list" aria-label={t('common.labels.required')}>
            {errors.map((error, idx) => (
              <li key={idx} className="step-error-item">
                {error.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Submit Button */}
      <Grid container spacing={2} className="payment-submit-row" alignItems="center">
        <Grid item xs={12} sm={6}>
          <button
            type="button"
            className={`step-continue-btn ${finishProgress ? 'step-continue-btn--disabled' : ''}`}
            disabled={finishProgress}
            onClick={handleSubmit}
            aria-busy={finishProgress}
            aria-label={finishProgress ? t('reservation.steps.paymentApproval.submit.ariaLabelProcessing') : t('reservation.steps.paymentApproval.submit.ariaLabelReady')}
          >
            {finishProgress ? t('reservation.steps.paymentApproval.submit.processing') : t('reservation.steps.paymentApproval.submit.label')}
          </button>
        </Grid>
        <Grid item xs={12} sm={6} className="payment-progress-cell">
          {finishProgress && (
            <CircularProgress className="payment-progress" aria-label={t('reservation.steps.paymentApproval.submit.progressLabel')} />
          )}
        </Grid>
      </Grid>
    </div>
  );
};

export default StepPaymentApproval;
